import React from 'react';
import { Button } from 'antd';
import { LeftOutlined, RightOutlined, EllipsisOutlined } from '@ant-design/icons';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  disabled = false
}) => {
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 7; 
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers();

  return (
    <div className="pagination-container">
      <Button
        icon={<LeftOutlined />}
        disabled={currentPage === 1 || disabled}
        onClick={handlePrevious}
        className="pagination-btn"
      >
        Previous
      </Button>

      <div className="pagination-numbers">
        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                <EllipsisOutlined />
              </span>
            );
          }
          
          return (
            <button
              key={page}
              className={`pagination-number ${page === currentPage ? 'active' : ''}`}
              onClick={() => onPageChange(page as number)}
              disabled={disabled}
            >
              {page}
            </button>
          );
        })}
      </div>

      {totalCount && (
        <span className="pagination-info">
          Total: {totalCount}
        </span>
      )}

      <Button
        disabled={currentPage === totalPages || disabled}
        onClick={handleNext}
        className="pagination-btn"
      >
        Next
        <RightOutlined />
      </Button>
    </div>
  );
};

export default Pagination;
